import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@nexirt/ui";
import { KpiPanel } from "./panels/KpiPanel";
import { PricingPanel } from "./panels/PricingPanel";
import { ProcessPanel } from "./panels/ProcessPanel";
import { CalendarPanel } from "./panels/CalendarPanel";
import { ProofPanel } from "./panels/ProofPanel";

/**
 * DynamicOutput — "The page doesn't have sections. It has answers."
 * Rev 25 Phase 2 Loop I. Static mocks for 5 panels; Phase 4 wires
 * Anthropic tool-use to populate them from the live conversation.
 *
 * Composer keyword matching: CopilotComposer's submit can dispatch
 * 'nexirt:copilot:show-panel' with { slug }; this component listens,
 * sets the active panel, scrolls to view, and one-shot pulses the
 * card. Same nx-care-pulse pattern as rev 23 /pricing#care.
 *
 * Phase 2 ships the visual + bridge. Phase 4 wires real tool-use.
 */

type PanelSlug = "kpis" | "pricing" | "process" | "calendar" | "proof";

type PromptRow = {
  slug: PanelSlug;
  num: string;
  text: string;
};

const PROMPTS: PromptRow[] = [
  {
    slug: "kpis",
    num: "01",
    text: "Show me what an ops dashboard for my field-service crew could look like.",
  },
  {
    slug: "pricing",
    num: "02",
    text: "What does a portal project cost end-to-end?",
  },
  {
    slug: "process",
    num: "03",
    text: "Walk me through how you actually deliver a project.",
  },
  {
    slug: "calendar",
    num: "04",
    text: "Book a 30-min discovery call with one of your engineers next week.",
  },
  {
    slug: "proof",
    num: "05",
    text: "Why should I trust this studio over an agency?",
  },
];

const PANEL_URLS: Record<PanelSlug, string> = {
  kpis: "show me ops dashboard kpis for last 30 days",
  pricing: "what does a portal project cost end-to-end?",
  process: "walk me through how you deliver",
  calendar: "book a 30-min discovery call",
  proof: "why should I trust this studio?",
};

declare global {
  interface WindowEventMap {
    "nexirt:copilot:show-panel": CustomEvent<{ slug: PanelSlug }>;
  }
}

export function DynamicOutput() {
  const [active, setActive] = useState<PanelSlug>("kpis");
  const [pulse, setPulse] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  // Listen for composer hand-off
  useEffect(() => {
    function onShow(e: Event) {
      const ce = e as CustomEvent<{ slug: PanelSlug }>;
      if (!ce.detail?.slug) return;
      setActive(ce.detail.slug);
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => setPulse(true), 600);
      setTimeout(() => setPulse(false), 2200);
    }
    window.addEventListener("nexirt:copilot:show-panel", onShow);
    return () => window.removeEventListener("nexirt:copilot:show-panel", onShow);
  }, []);

  const onPromptClick = useCallback((slug: PanelSlug) => {
    setActive(slug);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="border-t border-nx-line/60"
      data-dynamic-output
      aria-labelledby="dynamic-output-h"
    >
      <style>{PULSE_KEYFRAMES}</style>
      <div className="nx-container py-16 md:py-24">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-nx-full border border-nx-line bg-nx-surface/55 px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.18em] text-nx-text-faint">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-nx-accent-2"
                style={{ boxShadow: "0 0 8px var(--nx-accent-2)" }}
                aria-hidden
              />
              Generated on demand
            </span>
            <h2
              id="dynamic-output-h"
              className="mt-3 font-display text-[clamp(1.75rem,3vw,2.5rem)] font-semibold leading-tight tracking-tight text-nx-text"
            >
              The page doesn't have sections. It has answers.
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-nx-text-dim">
            Every prompt resolves to the smallest component that answers it
            — a chart, a price table, a calendar, a single line. No scrolling
            through marketing you don't need.
          </p>
        </div>

        <div
          className={cn(
            "mt-8 overflow-hidden rounded-nx3 border border-nx-line bg-nx-surface/40",
            pulse && "nx-dynamic-pulse-active",
          )}
          data-dynamic-output-card
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-3 border-b border-nx-line bg-nx-surface-2/60 px-3 py-2">
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#FF5F57]" aria-hidden />
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" aria-hidden />
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#28C840]" aria-hidden />
            </div>
            <div className="flex flex-1 items-center gap-2 truncate rounded-[6px] border border-nx-line bg-nx-bg/70 px-2.5 py-1 font-mono text-[11px] text-nx-text-faint">
              <span className="text-nx-accent-2">›</span>
              <span className="truncate">{PANEL_URLS[active]}</span>
            </div>
            <div className="hidden font-mono text-[10.5px] text-nx-text-faint sm:block">
              render · <b className="text-nx-text-dim">184ms</b>
            </div>
          </div>

          {/* Body — 2-col grid (left rail + canvas) */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2.5fr]">
            {/* Left rail */}
            <div className="border-b border-nx-line p-3 lg:border-b-0 lg:border-r">
              <div className="flex flex-col gap-1">
                {PROMPTS.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    role="tab"
                    aria-selected={active === p.slug}
                    data-dynamic-output-prompt
                    data-slug={p.slug}
                    onClick={() => onPromptClick(p.slug)}
                    className={cn(
                      "flex items-start gap-2.5 rounded-nx2 border-l-2 px-3 py-2.5 text-left transition-colors",
                      active === p.slug
                        ? "border-nx-accent bg-nx-accent-soft text-nx-text"
                        : "border-transparent text-nx-text-dim hover:bg-white/[0.03] hover:text-nx-text",
                    )}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-nx-text-faint shrink-0">
                      {p.num}
                    </span>
                    <span className="text-[12.5px] leading-snug">{p.text}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Canvas */}
            <div className="bg-nx-bg/30 p-4 md:p-6">
              <div className="mb-3 flex items-center justify-between font-mono text-[10.5px] uppercase tracking-[0.14em] text-nx-text-faint">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full bg-nx-accent-2"
                    style={{ boxShadow: "0 0 6px var(--nx-accent-2)" }}
                    aria-hidden
                  />
                  Streamed
                </span>
                <span data-dynamic-output-meta>
                  {active === "kpis" && "3 components · 12kb"}
                  {active === "pricing" && "3 tiers · 4kb"}
                  {active === "process" && "5 stages · 2kb"}
                  {active === "calendar" && "month + slots · 5kb"}
                  {active === "proof" && "stat row + bio · 3kb"}
                </span>
              </div>
              <div data-dynamic-output-canvas>
                {active === "kpis" && <KpiPanel />}
                {active === "pricing" && <PricingPanel />}
                {active === "process" && <ProcessPanel />}
                {active === "calendar" && <CalendarPanel />}
                {active === "proof" && <ProofPanel />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PULSE_KEYFRAMES = `
@keyframes nx-dynamic-pulse {
  0%   { outline: 3px solid color-mix(in srgb, var(--nx-accent-2) 0%, transparent); box-shadow: 0 0 0 0 color-mix(in srgb, var(--nx-accent-2) 0%, transparent); }
  20%  { outline: 3px solid color-mix(in srgb, var(--nx-accent-2) 65%, transparent); box-shadow: 0 0 0 12px color-mix(in srgb, var(--nx-accent-2) 25%, transparent); }
  100% { outline: 3px solid color-mix(in srgb, var(--nx-accent-2) 0%, transparent); box-shadow: 0 0 0 0 color-mix(in srgb, var(--nx-accent-2) 0%, transparent); }
}
.nx-dynamic-pulse-active {
  animation: nx-dynamic-pulse 1500ms cubic-bezier(0.22, 1, 0.36, 1) 1;
}
@media (prefers-reduced-motion: reduce) {
  .nx-dynamic-pulse-active { animation: none !important; }
}
`;

export default DynamicOutput;
